import { Scrollable } from '@pique/component';
import clsx from 'clsx';
import { selectAtom } from 'jotai/utils';
import type { HTMLAttributes, PropsWithChildren, Ref } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import type { ScrollSeekPlaceholderProps } from 'react-virtuoso';
import { Virtuoso } from 'react-virtuoso';

import { ListInnerWrapper } from './list';
import * as styles from './list.css';
import { ItemGroupHeader } from './page-group';
import {
  groupCollapseStateAtom,
  groupsAtom,
  listPropsAtom,
  ListProvider,
  useAtomValue,
} from './scoped-atoms';
import type {
  ItemGroupProps,
  ItemListHandle,
  ListItem,
  VirtualizedListProps,
} from './types';

type VirtuosoItemType =
  | 'sticky-header'
  | 'group-header'
  | 'item'
  | 'item-spacer';

interface BaseVirtuosoItem {
  type: VirtuosoItemType;
  key: string;
}

interface VirtuosoItemStickyHeader<T> extends BaseVirtuosoItem {
  type: 'sticky-header';
  data?: T;
}

interface VirtuosoItemItem<T> extends BaseVirtuosoItem {
  type: 'item';
  data: T;
}

interface VirtuosoItemGroupHeader<T> extends BaseVirtuosoItem {
  type: 'group-header';
  data: ItemGroupProps<T>;
}

interface VirtuosoPageItemSpacer extends BaseVirtuosoItem {
  type: 'item-spacer';
  data: {
    height: number;
  };
}

type VirtuosoItem<T> =
  | VirtuosoItemStickyHeader<T>
  | VirtuosoItemItem<T>
  | VirtuosoItemGroupHeader<T>
  | VirtuosoPageItemSpacer;

export const VirtualizedList = function VirtualizedList({ ref, ...props }: VirtualizedListProps<ListItem> & { ref?: Ref<ItemListHandle> }) {
  return (
    <ListProvider
      initialValues={
        [[listPropsAtom, props]] as Parameters<
          typeof ListProvider
        >[0]['initialValues']
      }
    >
      <ListInnerWrapper {...props} handleRef={ref}>
        <ListInner {...props} />
      </ListInnerWrapper>
    </ListProvider>
  );
};

const headingAtom = selectAtom(listPropsAtom, props => props?.heading);

const PageListHeading = () => {
  const heading = useAtomValue(headingAtom);
  return <div className={styles.heading}>{heading}</div>;
};

const useVirtuosoItems = <T extends ListItem>() => {
  const groups = useAtomValue(groupsAtom);
  const groupCollapsedState = useAtomValue(groupCollapseStateAtom);

  return useMemo(() => {
    const items: VirtuosoItem<T>[] = [];

    items.push({
      type: 'sticky-header',
      key: 'sticky-header',
    });

    for (const group of groups) {
      if (group.label) {
        items.push({
          type: 'group-header',
          key: `group-${group.id}`,
          data: group as ItemGroupProps<T>,
        });
      }
      if (!groupCollapsedState[group.id]) {
        const groupItems = group.items;
        const lastIndex = groupItems.length - 1;
        for (let i = 0; i < groupItems.length; i++) {
          const item = groupItems[i] as T;
          items.push({
            type: 'item',
            key: item.id,
            data: item,
          });
          if (i < lastIndex) {
            items.push({
              type: 'item-spacer',
              key: `spacer-item-${item.id}`,
              data: { height: 4 },
            });
          }
        }
      }

      items.push({
        type: 'item-spacer',
        key: `spacer-group-${group.id}`,
        data: { height: 8 },
      });
    }
    return items;
  }, [groupCollapsedState, groups]);
};

const Scroller = ({ ref, children, ...props }: PropsWithChildren<HTMLAttributes<HTMLDivElement>> & { ref?: Ref<HTMLDivElement> }) => {
  return (
    <Scrollable.Root>
      <Scrollable.Viewport {...props} ref={ref}>
        {children}
      </Scrollable.Viewport>
      <Scrollable.Scrollbar />
    </Scrollable.Root>
  );
};

const ScrollSeekPlaceholder = memo(function ScrollSeekPlaceholder({
  height,
  index,
}: ScrollSeekPlaceholderProps) {
  const isGroupHeader = index > 0 && height === 36;
  const isPageItem = height === 60;
  const isSpacer = height <= 8;

  if (isSpacer) {
    return <div style={{ height }} />;
  }

  if (isGroupHeader) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--list-side-padding)',
        }}
      >
        <div
          style={{
            height: 14,
            width: 120,
            borderRadius: 4,
            background: 'var(--affine-hover-color)',
          }}
        />
      </div>
    );
  }

  if (isPageItem) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--list-side-padding)',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 4,
            background: 'var(--affine-hover-color)',
            flexShrink: 0,
          }}
        />
        <div
          style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <div
            style={{
              height: 16,
              width: '60%',
              borderRadius: 4,
              background: 'var(--affine-hover-color)',
            }}
          />
          <div
            style={{
              height: 12,
              width: '40%',
              borderRadius: 4,
              background: 'var(--affine-hover-color)',
              opacity: 0.6,
            }}
          />
        </div>
      </div>
    );
  }

  return <div style={{ height }} />;
});

const scrollSeekConfig = {
  enter: (velocity: number) => Math.abs(velocity) > 1200,
  exit: (velocity: number) => Math.abs(velocity) < 400,
};

const ListInner = ({
  atTopStateChange,
  atTopThreshold,
  heading,
  headerRenderer,
  itemRenderer,
  ...props
}: VirtualizedListProps<ListItem>) => {
  const virtuosoItems = useVirtuosoItems<ListItem>();
  const [atTop, setAtTop] = useState(false);
  const handleAtTopStateChange = useCallback(
    (atTop: boolean) => {
      setAtTop(atTop);
      atTopStateChange?.(atTop);
    },
    [atTopStateChange]
  );
  const components = useMemo(() => {
    return {
      Header: heading ? PageListHeading : undefined,
      Scroller: Scroller,
      ScrollSeekPlaceholder,
    };
  }, [heading]);
  const itemContentRenderer = useCallback(
    (_index: number, data: VirtuosoItem<ListItem>) => {
      switch (data.type) {
        case 'sticky-header':
          return (
            headerRenderer?.(data.data) || (
              <div style={{ height: '1px' }} />
            )
          );
        case 'group-header':
          return <ItemGroupHeader {...data.data} />;
        case 'item':
          return itemRenderer?.(data.data);
        case 'item-spacer':
          return <div style={{ height: data.data.height }} />;
      }
    },
    [headerRenderer, itemRenderer]
  );
  const computeItemKey = useCallback(
    (_index: number, item: VirtuosoItem<ListItem>) => item.key,
    []
  );
  return (
    <Virtuoso<VirtuosoItem<ListItem>>
      data-has-scroll-top={!atTop}
      atTopThreshold={atTopThreshold ?? 0}
      atTopStateChange={handleAtTopStateChange}
      components={components}
      data={virtuosoItems}
      data-testid="virtualized-page-list"
      data-total-count={props.items.length}
      topItemCount={0}
      totalCount={virtuosoItems.length}
      itemContent={itemContentRenderer}
      computeItemKey={computeItemKey}
      defaultItemHeight={60}
      overscan={{ main: 200, reverse: 100 }}
      increaseViewportBy={{ top: 200, bottom: 400 }}
      scrollSeekConfiguration={scrollSeekConfig}
      className={clsx(props.className, styles.root)}
    />
  );
};
